/**
 * PostHog — analytics de produto, funis, retenção e *feature flags* (§9.10).
 *
 * 🔴 **Instalado, mas não inicializado antes do consentimento.** O Cookiebot bloqueia o PostHog até
 * o utilizador aceitar (T0.14, verificado na T0.19). Inicializar antes seria recolher dados sem base
 * legal.
 *
 * É também a razão pela qual o gate de versão da T0.4b **não** é uma feature flag: segurança de
 * plataforma não pode depender de consentimento de analytics.
 */

export interface PostHogConfig {
  apiKey: string;
  host: string;
}

export type InitResult =
  | { iniciado: true; modo: 'inicializado' | 'reativado' }
  | {
      iniciado: false;
      motivo: 'sem_consentimento' | 'sem_chave' | 'ja_iniciado';
    };

export interface PostHogLifecycleState {
  /** Captura ativa agora. */
  jaIniciado: boolean;
  /** O singleton do SDK já recebeu `init()` nesta sessão. */
  inicializadoAntes?: boolean;
}

export interface InitPostHogInput {
  consentimento: boolean;
  config: PostHogConfig;
  /** Injetado para permitir teste sem o SDK. Em produção, `posthog.init`. */
  iniciar: (apiKey: string, opcoes: Record<string, unknown>) => void;
  /** Após revogação o SDK já existe: reativar evita duplicar listeners com um segundo `init()`. */
  reativar: () => void;
  estado?: PostHogLifecycleState;
}

const estadoGlobal: PostHogLifecycleState = { jaIniciado: false, inicializadoAntes: false };

export function initPostHog({
  consentimento,
  config,
  iniciar,
  reativar,
  estado = estadoGlobal,
}: InitPostHogInput): InitResult {
  if (!consentimento) return { iniciado: false, motivo: 'sem_consentimento' };

  // A chave nasce vazia no `.env` até a conta existir. Chamar o SDK com string vazia produziria
  // um erro obscuro em runtime em vez de um motivo legível aqui.
  if (!config.apiKey) return { iniciado: false, motivo: 'sem_chave' };

  if (estado.jaIniciado) return { iniciado: false, motivo: 'ja_iniciado' };

  if (estado.inicializadoAntes) {
    reativar();
    estado.jaIniciado = true;
    return { iniciado: true, modo: 'reativado' };
  }

  iniciar(config.apiKey, {
    api_host: config.host,
    // O pageview automático dispara antes de o router resolver, atribuindo o evento à URL errada.
    // A instrumentação de produto é da T0.19.
    capture_pageview: false,
    // Só eventos do catálogo tipado podem sair; cliques/inputs automáticos escapariam desse contrato.
    autocapture: false,
    // Session replay é opt-in por desenho: grava o ecrã do utilizador.
    disable_session_recording: true,
    // Sem consentimento não se chega aqui; ainda assim, o SDK não deve persistir antes do init.
    persistence: 'localStorage+cookie',
    // Defesa em profundidade: mesmo que o SDK seja carregado por engano, nasce sem capturar. O
    // aceite explícito abaixo é a única transição que o libera.
    opt_out_capturing_by_default: true,
  });

  reativar();

  estado.jaIniciado = true;
  estado.inicializadoAntes = true;
  return { iniciado: true, modo: 'inicializado' };
}

/** Forma mínima do SDK exigida pela revogação. */
export interface PostHogOptOutClient {
  opt_out_capturing(): void;
  reset(resetDeviceId?: boolean): void;
}

export interface OptOutPostHogInput {
  /** `null` quando o SDK nunca foi carregado — recusar sem antes aceitar é caminho normal. */
  cliente: PostHogOptOutClient | null;
  estado?: PostHogLifecycleState;
}

/**
 * Revoga o consentimento de analytics (RGPD **Art. 7(3)**).
 *
 * Retirar o consentimento tem de ser tão fácil quanto dá-lo. Duas ações, não uma:
 *
 * 1. `reset(true)` — **substitui a identidade local**, incluindo o *device id*;
 * 2. `opt_out_capturing()` — para de enviar eventos e persiste a recusa por último. O PostHog
 *    limpa o próprio estado de consentimento durante `reset()`, portanto inverter esta ordem
 *    reativaria silenciosamente a captura.
 *
 * O PostHog persiste o *distinct id* no `localStorage`, e
 *    parar a captura sem o apagar seria conformidade incompleta: o identificador continuaria no
 *    dispositivo de quem pediu para sair.
 *
 * Nunca lança. Uma falha do SDK não pode impedir o chamador de registar a revogação — o utilizador
 * pediu para sair, e o pedido vale independentemente de o SDK cooperar.
 */
export function optOutPostHog({ cliente, estado = estadoGlobal }: OptOutPostHogInput): void {
  // A captura fica inativa, mas o singleton permanece inicializado. Se o consentimento voltar,
  // `opt_in_capturing()` reativa o mesmo cliente sem duplicar listeners com outro `init()`.
  if (estado.jaIniciado) estado.inicializadoAntes = true;
  estado.jaIniciado = false;

  if (!cliente) return;

  try {
    cliente.reset(true);
  } catch {
    // A tentativa de opt-out abaixo é independente: falhar a limpeza nunca pode manter captura.
  }

  try {
    cliente.opt_out_capturing();
  } catch {
    // O chamador ainda registou a recusa no armazenamento autoritativo do produto.
  }
}

/**
 * As quatro *feature flags* previstas.
 *
 * Existem porque a arquitetura precisa **absorver indecisões conhecidas**: a regra de reembolso da
 * §21.1 tem três opções em aberto e **não pode ser hard-coded**. Declará-las agora é o que evita
 * descobrir a indecisão no meio do marco que a consome.
 *
 * ✅ Criadas no dashboard em 2026-08-31, **todas desligadas**. Enquanto assim estiverem, o `default`
 * abaixo é o valor efetivo — e ligar uma antes do marco que a consome produziria comportamento que
 * ninguém escreveu.
 *
 * 🔴 **O método de leitura difere conforme o tipo**, e isso importa para quem as consumir:
 *
 * - `trial_requires_card` é **Boolean** → `isFeatureEnabled` / `getFeatureFlag`;
 * - as outras três são **Remote config** → **só** `getFeatureFlagPayload`. Nestas, o próprio
 *   PostHog avisa que `getFeatureFlag` devolve sempre `true`, o que tornaria um `if (flag)`
 *   silenciosamente errado.
 *
 * O payload das três de config está em `null` no dashboard, espelhando o `default` daqui: um valor
 * de exemplo seria pior que nenhum, porque alguém poderia ligá-las e receber números que nunca
 * passaram por decisão de produto.
 */
export const FEATURE_FLAGS = {
  /** Pesos do algoritmo de ranking, ajustáveis sem deploy. */
  ranking_weights: { default: null, marco: 'M2', tipo: 'remote_config' },
  /** Critérios do Selo Powerful (§22.2, em aberto). */
  seal_criteria: { default: null, marco: 'M2', tipo: 'remote_config' },
  /** Mecânica do trial pós-Founders (§21.2, em aberto). */
  trial_requires_card: { default: false, marco: 'M5', tipo: 'boolean' },
  /** Reembolso quando o profissional cancela (§21.1, três opções em aberto). */
  pro_cancel_refund_policy: { default: null, marco: 'M3', tipo: 'remote_config' },
} as const satisfies Record<
  string,
  { default: unknown; marco: string; tipo: 'boolean' | 'remote_config' }
>;

export type FeatureFlagName = keyof typeof FEATURE_FLAGS;
